import { useEffect } from "react";

const SITE = "https://csrjournal.org";

function fmtAuthor(au) {
  const last = [au.last_name, au.second_last_name].filter(Boolean).join(" ").trim();
  const given = [au.first_name, au.middle_name].filter(Boolean).join(" ").trim();
  if (last && given) return `${last}, ${given}`;
  return last || given || "";
}

function toYMD(raw) {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())}`;
}

// Injects Highwire Press (Google Scholar) citation meta tags for a published article.
// Tags are removed on unmount / article change so they never leak to other pages.
export default function ScholarMeta({ article: a }) {
  useEffect(() => {
    const added = [];
    const add = (name, content) => {
      if (!content) return;
      const m = document.createElement("meta");
      m.setAttribute("name", name);
      m.setAttribute("content", content);
      document.head.appendChild(m);
      added.push(m);
    };

    // Only public, published, non-hidden, non-draft articles get citation tags.
    if (a && a.published && !a.hidden) {
      const canonical = `${SITE}/articles/${a.slug}`;
      add("citation_title", a.title);
      (a.authors || []).forEach((au) => add("citation_author", fmtAuthor(au)));
      add("citation_publication_date", toYMD(a.publication_date || a.published_at));
      add("citation_journal_title", "Clinical & Surgical Review");
      add("citation_volume", a.volume);
      add("citation_issue", a.issue);

      // First/last page: use page range, else article number/id as elocation.
      let first = "", last = "";
      if (a.pages && a.pages.includes("-")) {
        [first, last] = a.pages.split("-").map((s) => s.trim());
      } else if (a.pages) {
        first = last = a.pages.trim();
      } else if (a.article_number) {
        first = last = a.article_number.trim();
      }
      add("citation_firstpage", first);
      add("citation_lastpage", last);

      // Only output a DOI when a real registered DOI exists.
      if (a.doi && a.doi.trim()) add("citation_doi", a.doi.trim());

      add("citation_abstract_html_url", canonical);
      if (a.pdf_url) add("citation_pdf_url", `${SITE}${a.pdf_url}`);
      add("citation_language", a.language || "en");
      if (a.keywords && a.keywords.length) add("citation_keywords", a.keywords.join("; "));
    }

    return () => added.forEach((m) => m.remove());
  }, [a]);

  return null;
}
